import { ReactElement, ReactNode, useCallback, useMemo } from 'react';
import { SelectProps as AntSelectProps } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { Select } from 'common/components/antd/Select/Select';
import { TFilterFunc } from './types/EnhancedSelect.type';

export type EnhancedSelectOption = {
  label: string;
  value: string | number | null | object;
  render?: (args: { label: string; value: string | number | object | null }) => ReactNode;
};

export type EnhancedSelectOptions = EnhancedSelectOption[];

export type EnhencedSelectProps = Omit<AntSelectProps, 'options' | 'size'> & {
  options: EnhancedSelectOptions;
  size?: 'small' | 'middle' | 'large';
};

export const EnhancedSelect = ({ options, ...restProps }: EnhencedSelectProps): ReactElement => {
  const mergedOptions = useMemo(
    () =>
      options.map(({ value, label, render, ...rest }) => ({
        label:
          render?.({
            value,
            label,
          }) || label,
        value,
        keywords: [label, ...Object.values(rest)],
      })),
    [options],
  );

  const handleFilterOption: TFilterFunc<DefaultOptionType> = useCallback(
    (inputValue: string, option: DefaultOptionType | undefined): boolean => {
      if (!Array.isArray(option?.keywords) || typeof option === 'undefined' || !inputValue) {
        return false;
      }
      for (let i = 0; i < option.keywords.length; i += 1) {
        const keyword = option.keywords[i];
        if (typeof keyword === 'string') {
          if (keyword.toLowerCase().includes(inputValue.toLowerCase())) {
            return true;
          }
        }
      }
      return false;
    },
    [],
  );

  return (
    <Select
      rootClassName="enhanced-ant-select"
      popupClassName="enhanced-ant-select-popup"
      showSearch
      filterOption={handleFilterOption}
      {...restProps}
    >
      {mergedOptions.map((option) => (
        <Select.Option
          key={typeof option.value === 'object' ? JSON.stringify(option.value) : option.value}
          value={option.value}
        >
          {option.label}
        </Select.Option>
      ))}
    </Select>
  );
};
